import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { ChevronRight, ArrowLeft, Layers, Network } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EntityRelationshipGraph, EntityRelationship } from './EntityRelationshipGraph';
import { FieldLevelGraph } from './FieldLevelGraph';
import { buildHierarchicalGraph, filterRelationshipsForEntity } from '@/lib/utils/relationshipParser';
import type { DrillDownLevel, HierarchicalNode } from '@/types/relationshipDetails';
import { objectsStore } from '@/lib/stores/objectsStore';
import { dialogStore } from '@/lib/stores/dialogStore';
import { typesStore } from '@/lib/stores/typesStore';
import { documentStore } from '@/lib/stores/documentStore';
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

interface DrillDownRelationshipGraphProps {
  relationships: EntityRelationship[];
  width?: number;
  height?: number;
  initialEntityId?: string;
  initialEntityType?: string;
  enableDrillDown?: boolean;
  disableZoomCompensation?: boolean;
}

/**
 * DrillDownRelationshipGraph Component
 * 
 * Provides hierarchical visualization of entity relationships
 * with drill-down capability to see field-level connections
 */
export const DrillDownRelationshipGraph: React.FC<DrillDownRelationshipGraphProps> = ({
  relationships,
  width = 1200,
  height = 800,
  initialEntityId,
  initialEntityType,
  enableDrillDown = true,
  disableZoomCompensation = false,
}) => {
  const { t } = usePlatformTranslation();
  
  // Navigation state
  const [drillDownStack, setDrillDownStack] = useState<DrillDownLevel[]>([
    { level: 'entity' }
  ]);

  // Initialize with specific entity if provided
  useEffect(() => {
    if (initialEntityId && initialEntityType && enableDrillDown) {
      // Load entity data
      fetchEntityData(initialEntityId, initialEntityType).then((entityData) => {
        if (entityData) {
          setDrillDownStack([
            { level: 'entity' },
            {
              level: 'field',
              entityId: initialEntityId,
              entityType: initialEntityType,
              entityName: entityData.name || initialEntityId,
            },
          ]);
        }
      });
    }
  }, [initialEntityId, initialEntityType, enableDrillDown]);

  const currentLevel = drillDownStack[drillDownStack.length - 1];

  // Build entity map for field-level rendering
  const entityMap = useMemo(() => {
    const map = new Map<string, any>();
    
    // This will be populated as entities are loaded
    // For now, we'll fetch them on-demand when drilling down
    
    return map;
  }, []);

  // Fetch entity data helper
  const fetchEntityData = async (entityId: string, entityType: string): Promise<any> => {
    try {
      switch (entityType) {
        case 'glossary': {
          const objects = objectsStore.getData();
          return objects.find((obj: any) => obj.id === entityId);
        }
        case 'dialog': {
          const dialogs = dialogStore.getData();
          return dialogs.find((dialog: any) => dialog.id === entityId);
        }
        case 'variable': {
          const types = typesStore.getData();
          return types.find((type: any) => type.id === entityId);
        }
        case 'document': {
          const documents = documentStore.getData();
          return documents.find((doc: any) => doc.id === entityId);
        }
        default:
          return null;
      }
    } catch (error) {
      console.error('Error fetching entity data:', error);
      return null;
    }
  };

  // Handle entity click for drill-down
  const handleEntityClick = useCallback(
    async (entityId: string, entityType: string) => {
      if (!enableDrillDown) return;

      // Fetch entity data
      const entityData = await fetchEntityData(entityId, entityType);
      
      if (entityData) {
        entityMap.set(entityId, entityData);
        
        setDrillDownStack((prev) => [
          ...prev,
          {
            level: 'field',
            entityId,
            entityType,
            entityName: entityData.name || entityId,
          },
        ]);
      }
    },
    [enableDrillDown, entityMap]
  );

  // Handle field-level node click
  const handleFieldNodeClick = useCallback(
    (node: HierarchicalNode) => {
      // Could implement further drill-down if needed
      console.log('Field node clicked:', node);
    },
    []
  );

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (drillDownStack.length > 1) {
      setDrillDownStack((prev) => prev.slice(0, -1));
    }
  }, [drillDownStack.length]);

  // Handle breadcrumb click
  const handleBreadcrumbClick = useCallback((index: number) => {
    setDrillDownStack((prev) => prev.slice(0, index + 1));
  }, []);

  // Prepare field-level graph data
  const fieldLevelData = useMemo(() => {
    if (currentLevel.level !== 'field' || !currentLevel.entityId) {
      return { nodes: [], links: [] };
    }

    const entityRelationships = filterRelationshipsForEntity(
      relationships,
      currentLevel.entityId
    );

    // Load related entities into the map
    const loadRelatedEntities = async () => {
      const relatedEntityIds = new Set<{ id: string; type: string }>();
      
      entityRelationships.forEach((rel) => {
        if (rel.source_id !== currentLevel.entityId) {
          relatedEntityIds.add({ id: rel.source_id, type: rel.source_type });
        }
        if (rel.target_id !== currentLevel.entityId) {
          relatedEntityIds.add({ id: rel.target_id, type: rel.target_type });
        }
      });

      for (const { id, type } of relatedEntityIds) {
        if (!entityMap.has(id)) {
          const data = await fetchEntityData(id, type);
          if (data) {
            entityMap.set(id, data);
          }
        }
      }
    };

    // Load entities asynchronously
    loadRelatedEntities();

    return buildHierarchicalGraph(entityRelationships, entityMap);
  }, [currentLevel, relationships, entityMap]);

  // Render breadcrumb navigation
  const renderBreadcrumbs = () => {
    return (
      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-4">
        {drillDownStack.map((level, index) => (
          <React.Fragment key={index}>
            {index > 0 && <ChevronRight className="w-4 h-4" />}
            <button
              onClick={() => handleBreadcrumbClick(index)}
              className={`hover:text-foreground transition-colors ${
                index === drillDownStack.length - 1 ? 'text-foreground font-medium' : ''
              }`}
            >
              {level.level === 'entity' ? 'All Entities' : level.entityName || 'Entity'}
            </button>
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Calculate adjusted height for breadcrumbs and controls
  const graphHeight = enableDrillDown && drillDownStack.length > 1 ? height - 60 : height;

  return (
    <div className="w-full h-full flex flex-col">
      {/* Breadcrumbs and controls */}
      {enableDrillDown && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {drillDownStack.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back
                </Button>
              )}
              {renderBreadcrumbs()}
            </div>
            
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {currentLevel.level === 'entity' ? (
                <>
                  <Network className="w-4 h-4" />
                  <span>Entity Level</span>
                </>
              ) : (
                <>
                  <Layers className="w-4 h-4" />
                  <span>Field Level</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Graph visualization */}
      <div className="flex-1 min-h-0">
        {currentLevel.level === 'entity' ? (
          <EntityRelationshipGraph
            relationships={relationships}
            width={width}
            height={graphHeight}
            initialSelectedNodeId={initialEntityId}
            showControls={true}
            showLegend={true}
            disableZoomCompensation={disableZoomCompensation}
            onNodeClick={enableDrillDown ? handleEntityClick : undefined}
          />
        ) : (
          <FieldLevelGraph
            nodes={fieldLevelData.nodes}
            links={fieldLevelData.links}
            width={width}
            height={graphHeight}
            onNodeClick={handleFieldNodeClick}
            highlightedNodeId={currentLevel.entityId}
          />
        )}
      </div>
    </div>
  );
};
